// shared/ui/ImagenesSection.jsx  (presentacional). Los object URLs de vista previa se crean/revocan aquí
// (API del navegador, efecto ligado al ciclo de vida del thumbnail) para que application quede libre de DOM.
import React from 'react';
import SectionCard from './SectionCard.jsx';
import PillButton from './PillButton.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import ImagenGuardada from './ImagenGuardada.jsx';
import ImagenPendiente from './ImagenPendiente.jsx';
import { OPACIDAD_DESHABILITADO } from './opacidad';
import { textoPendientesEliminar, MAX_IMAGENES, MSG_LIMITE_ALCANZADO, tituloImagenes } from '../domain/media';

const THUMB = { width: 120 };

// saved: media[] del detalle (con `eliminacion` si está marcada); pending: [{uuid,file,estado,error}] (estado lo maneja application)
const ImagenesSection = ({
  saved,
  pending,
  total,
  limiteAlcanzado = false,
  subtitle = 'Evidencia fotográfica de la capacitación.',
  entidad = 'capacitación',
  disabled = false,
  onFiles,
  onRemove,
  onRetry,
  onPendingDone,
  onMarkDelete,
  onUndoDelete,
}) => {
  // Esta sección solo sabe qué imagen se quiere eliminar. Al confirmar NO se llama al API: `onMarkDelete(uuid)` la marca
  // y se elimina al guardar el formulario.
  const [toDelete, setToDelete] = React.useState(null);

  const pendientesEliminar = saved.filter(media => media.eliminacion).length;
  const textoPendientes = textoPendientesEliminar(pendientesEliminar);

  const closeDialog = () => setToDelete(null);

  const confirmDelete = () => {
    onMarkDelete(toDelete);
    closeDialog();
  };

  const inputRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);

  const bloqueado = disabled || limiteAlcanzado;
  const openPicker = () => !bloqueado && inputRef.current && inputRef.current.click();
  const handleFiles = files => {
    // Copia: el FileList del input se vacía al resetear su value y la validación es asíncrona.
    const lista = Array.from(files || []);
    if (lista.length) onFiles(lista);
  };

  return (
    <SectionCard
      title={tituloImagenes(total)}
      subtitle={
        <>
          {subtitle}
          <div role="status" aria-live="polite" style={{ color: '#6c757d', fontSize: 12 }}>
            {textoPendientes}
          </div>
        </>
      }
      actions={
        <span
          title={limiteAlcanzado ? MSG_LIMITE_ALCANZADO : undefined}
          style={{ display: 'inline-block', cursor: bloqueado ? 'default' : undefined }}>
          <PillButton icon="fa-plus" onClick={openPicker} disabled={bloqueado} style={bloqueado ? { pointerEvents: 'none' } : undefined}>
            Agregar Imagen
          </PillButton>
        </span>
      }>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
        style={{ display: 'none' }}
        onChange={event => {
          handleFiles(event.target.files);
          event.target.value = ''; // permite volver a elegir el mismo archivo
        }}
      />
      <div className="d-flex flex-wrap align-items-stretch" style={{ gap: 12 }}>
        {saved.map(media => (
          <ImagenGuardada
            key={media.uuid || media.id}
            media={media}
            size={THUMB.width}
            disabled={disabled}
            canDelete
            onDelete={setToDelete}
            onUndo={onUndoDelete}
          />
        ))}
        {pending.map(item => (
          <ImagenPendiente key={item.uuid} item={item} disabled={disabled} onRemove={onRemove} onRetry={onRetry} onDone={onPendingDone} />
        ))}
        <div
          role="button"
          tabIndex={bloqueado ? -1 : 0}
          aria-disabled={bloqueado ? 'true' : 'false'}
          onClick={openPicker}
          onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && openPicker()}
          onDragOver={event => {
            event.preventDefault();
            if (!bloqueado) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={event => {
            event.preventDefault();
            setDragging(false);
            if (!bloqueado) handleFiles(event.dataTransfer.files);
          }}
          style={{
            flex: '1 1 260px',
            minHeight: 120,
            border: `2px dashed ${dragging && !bloqueado ? '#789D16' : '#c9ccd3'}`,
            borderRadius: 12,
            background: dragging && !bloqueado ? '#f4f8e8' : 'transparent',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 16,
            cursor: bloqueado ? 'default' : 'pointer',
            opacity: limiteAlcanzado ? OPACIDAD_DESHABILITADO : 1,
            transition: 'opacity 150ms',
          }}>
          <i className="fa fa-cloud-upload-alt fa-2x mb-2" style={{ color: '#6c757d' }} />
          <strong>Imágenes</strong>
          <span style={{ color: '#6c757d', fontSize: 13 }}>
            {limiteAlcanzado
              ? MSG_LIMITE_ALCANZADO
              : `Arrastra los archivos aquí o haz clic para adjuntar desde tu equipo · JPG, PNG hasta 25 MB · máximo ${MAX_IMAGENES} imágenes`}
          </span>
        </div>
      </div>
      <ConfirmDialog
        open={!!toDelete}
        title="¿Confirmas que quieres eliminar esta imagen?"
        description={`La imagen se eliminará de esta ${entidad} al guardar el registro. Podrás deshacerlo antes de guardar.`}
        onConfirm={confirmDelete}
        onCancel={closeDialog}
      />
    </SectionCard>
  );
};

export default ImagenesSection;
